/**
 * WRMS Notification Service — Digi Sahyadri
 * Writes in-app notifications to Firestore.
 * Email is stubbed — ready for Cloud Functions integration.
 */

import { db } from '../../config/firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  orderBy,
  serverTimestamp,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';

const NOTIF_COLLECTION = 'wrms_notifications';
const USERS_COLLECTION = 'employees';

// ── In-App Notification ──────────────────────────────────────────────────────

/**
 * Send a notification to a specific user by employeeId
 */
export const sendNotificationToUser = async (recipientId, payload) => {
  try {
    await addDoc(collection(db, NOTIF_COLLECTION), {
      recipientId,
      recipientType: 'user',
      ...payload,
      read: false,
      createdAt: serverTimestamp(),
    });

    // Email stub — log for now, wire to Cloud Functions later
    console.info(`[EMAIL STUB] To employee ${recipientId}:`, payload.title, '-', payload.message);
  } catch (error) {
    console.error('[notificationService] sendNotificationToUser error:', error);
  }
};

/**
 * Send a notification to all users with a given role.
 * Queries employees collection to find matching users.
 */
export const sendNotificationToRole = async (role, payload, department = null) => {
  try {
    const normalizedRole = (role || '').toLowerCase().trim();
    // Build the notification for the role — we also write a role-broadcast record
    await addDoc(collection(db, NOTIF_COLLECTION), {
      recipientId: null,
      recipientRole: normalizedRole,
      recipientDepartment: department || null,
      recipientType: 'role',
      ...payload,
      read: false,
      createdAt: serverTimestamp(),
    });

    console.info(`[NOTIF] To role "${normalizedRole}" (dept: ${department}):`, payload.title);
  } catch (error) {
    console.error('[notificationService] sendNotificationToRole error:', error);
  }
};

/**
 * Get notifications for a specific user (includes both direct + role-based)
 */
export const getNotificationsForUser = async (userId, userRole, limit = 30, userDept = null, userIdsList = []) => {
  try {
    const allIds = Array.from(new Set([
      ...(Array.isArray(userId) ? userId : [userId]),
      ...(Array.isArray(userIdsList) ? userIdsList : [])
    ].filter(Boolean).map(String)));

    // Direct notifications for this user (handles auth UID, employee doc ID, employee code)
    const directQ = allIds.length > 1
      ? query(
          collection(db, NOTIF_COLLECTION),
          where('recipientId', 'in', allIds.slice(0, 10))
        )
      : query(
          collection(db, NOTIF_COLLECTION),
          where('recipientId', '==', allIds[0] || userId)
        );

    // Role-based broadcasts (support normalized case)
    const roleLower = (userRole || '').toLowerCase().trim();
    const rolesToMatch = Array.from(new Set([userRole, roleLower].filter(Boolean)));
    const roleQ = query(
      collection(db, NOTIF_COLLECTION),
      where('recipientRole', 'in', rolesToMatch.length > 0 ? rolesToMatch : ['none']),
      where('recipientType', '==', 'role')
    );

    const [directSnap, roleSnap] = await Promise.all([
      getDocs(directQ),
      getDocs(roleQ),
    ]);

    const notifications = [];
    const seen = new Set();

    directSnap.forEach(d => {
      if (!seen.has(d.id)) {
        seen.add(d.id);
        notifications.push({ id: d.id, ...d.data() });
      }
    });

    roleSnap.forEach(d => {
      if (!seen.has(d.id)) {
        const data = d.data();

        // Department check for Dept Head
        if (userRole === 'dept_head' && data.recipientDepartment && userDept) {
          if (!isDeptVisibleToUser(data.recipientDepartment, data.recipientDepartment, { role: userRole, department: userDept })) {
            return; // Skip notifications for other departments!
          }
        }

        seen.add(d.id);
        // Check if this user has already read this role notification
        const readBy = data.readBy || [];
        notifications.push({
          id: d.id,
          ...data,
          read: readBy.includes(userId),
        });
      }
    });

    return notifications
      .sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      })
      .slice(0, limit);
  } catch (error) {
    console.error('[notificationService] getNotificationsForUser error:', error);
    return [];
  }
};

/**
 * Real-time listener for user notifications (for the bell counter)
 */
export const subscribeToNotifications = (userId, userRole, userDept = null, callback = null, userIdsList = []) => {
  // Support both (userId, userRole, callback) and (userId, userRole, userDept, callback, userIdsList)
  const cb = typeof userDept === 'function' ? userDept : callback;
  const dept = typeof userDept === 'string' ? userDept : null;

  const allIds = Array.from(new Set([
    ...(Array.isArray(userId) ? userId : [userId]),
    ...(Array.isArray(userIdsList) ? userIdsList : [])
  ].filter(Boolean).map(String)));

  // Listen for direct notifications (supports auth UID, employee doc ID, employee code)
  const q = allIds.length > 1
    ? query(
        collection(db, NOTIF_COLLECTION),
        where('recipientId', 'in', allIds.slice(0, 10)),
        where('read', '==', false)
      )
    : query(
        collection(db, NOTIF_COLLECTION),
        where('recipientId', '==', allIds[0] || userId),
        where('read', '==', false)
      );

  const roleLower = (userRole || '').toLowerCase().trim();
  const rolesToMatch = Array.from(new Set([userRole, roleLower].filter(Boolean)));
  const roleQ = query(
    collection(db, NOTIF_COLLECTION),
    where('recipientRole', 'in', rolesToMatch.length > 0 ? rolesToMatch : ['none']),
    where('recipientType', '==', 'role')
  );

  let directData = [];
  let roleData = [];
  let directInitialDone = false;
  let roleInitialDone = false;

  const merge = () => {
    const isInitialSync = !directInitialDone || !roleInitialDone;
    const seen = new Set();
    const all = [];
    [...directData, ...roleData].forEach(n => {
      if (!seen.has(n.id)) {
        seen.add(n.id);
        all.push(n);
      }
    });
    if (cb) {
      cb(all.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      }), isInitialSync);
    }
  };

  const unsub1 = onSnapshot(q, snap => {
    directData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    directInitialDone = true;
    merge();
  }, err => {
    console.error('[notifService] direct listener error:', err);
    directInitialDone = true;
    merge();
  });

  const unsub2 = onSnapshot(roleQ, snap => {
    roleData = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(n => {
        // Department check for Dept Head
        if (userRole === 'dept_head' && n.recipientDepartment && dept) {
          if (!isDeptVisibleToUser(n.recipientDepartment, n.recipientDepartment, { role: userRole, department: dept })) {
            return false;
          }
        }
        const readBy = n.readBy || [];
        return !readBy.includes(userId);
      });
    roleInitialDone = true;
    merge();
  }, err => {
    console.error('[notifService] role listener error:', err);
    roleInitialDone = true;
    merge();
  });

  // Return combined unsubscribe
  return () => {
    unsub1();
    unsub2();
  };
};

/**
 * Mark a notification as read for this user
 */
export const markNotificationRead = async (notifId, userId, recipientType) => {
  try {
    const ref = doc(db, NOTIF_COLLECTION, notifId);
    if (recipientType === 'role') {
      // For role notifications, track who has read it
      const snap = await getDocs(query(
        collection(db, NOTIF_COLLECTION),
        where('__name__', '==', notifId)
      ));
      const data = snap.docs[0]?.data() || {};
      const readBy = data.readBy || [];
      if (!readBy.includes(userId)) {
        await updateDoc(ref, { readBy: [...readBy, userId] });
      }
    } else {
      await updateDoc(ref, { read: true });
    }
  } catch (error) {
    console.error('[notificationService] markNotificationRead error:', error);
  }
};

/**
 * Mark all notifications read for a user
 */
export const markAllNotificationsRead = async (userId, notifIds) => {
  try {
    const batch = writeBatch(db);
    notifIds.forEach(id => {
      const ref = doc(db, NOTIF_COLLECTION, id);
      batch.update(ref, { read: true });
    });
    await batch.commit();
  } catch (error) {
    console.error('[notificationService] markAllRead error:', error);
  }
};

/**
 * Send a 24h reminder to pending approvers strictly within the hierarchy
 */
export const sendPendingReminder = async (request, approverRole) => {
  const payload = {
    type: 'reminder',
    title: '⏰ Pending Approval Reminder',
    message: `${request.employeeName}'s ${request.requestType} request (${request.requestId}) has been pending for over 24 hours. Please review.`,
    requestId: request.id,
    requestType: request.requestType,
    requestDocId: request.requestId,
  };

  if (approverRole === 'manager' && request.reportingManagerId) {
    await sendNotificationToUser(request.reportingManagerId, payload);
  } else if (approverRole === 'handover' && request.handoverToId) {
    await sendNotificationToUser(request.handoverToId, payload);
  } else if (approverRole && approverRole !== 'manager' && approverRole !== 'handover') {
    await sendNotificationToRole(approverRole, payload, request.department);
  }
};
